import { put, takeEvery, select } from "redux-saga/effects";
import {
  NMAxiosInst,
  SagaRegistry,
  setAlertAndLoading,
  requestHandler,
} from "@neumetric/blueprint-lib/store";
import { auditorActions } from "./slice";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";

function* fetchModelList() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM");
    yield put(auditorActions.setModelList(res?.data));
  }, "Failed to fetch Models");
}

function* fetchReportFormats() {
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM/report_formats");
    yield put(auditorActions.setReportFormats(res?.report_formats));
  }, "Failed to fetch Report Formats");
}

function buildFormDataFromPayload(payload) {
  const fd = new FormData();

  (payload?.formdata || []).forEach((item) => {
    if (!item?.key) return;

    if (item.type === "file") {
      if (item.value instanceof File || item.value instanceof Blob) {
        fd.append(item.key, item.value);
      }
    } else {
      fd.append(item.key, item.value ?? "");
    }
  });

  // DEBUG: confirm files exist
  for (const [k, v] of fd.entries()) {
    console.log("FD:", k, v instanceof File ? `File(${v.name})` : v);
  }

  return fd;
}

function* createModel(action) {
  const options = {
    errorMessage: "Failed to create Model",
    action,
  };

  yield requestHandler(function* () {
    const formData = buildFormDataFromPayload(action.payload);

    yield NMAxiosInst.post(
      "/LM/compliance_import",
      formData,
      NMAxiosInst.getFormDataContentType(),
    );

    yield setAlertAndLoading(null, {
      message: "Audit Blueprint Imported Successfully",
    });
  }, options);
}

function* updateModel(action) {
  const options = {
    action: action,
    errorMessage: "Failed to update Model",
  };
  yield requestHandler(function* () {
    const formData = buildFormDataFromPayload(action.payload);

    const res = yield NMAxiosInst.post(
      "/LM/compliance_update",
      formData,
      NMAxiosInst.getFormDataContentType(),
    );

    yield setAlertAndLoading(null, {
      message: res?.message || "Compliance updated successfully.",
    });

    // Refresh list
    yield put(auditorActions.fetchModelList());

    // Refresh details (important for edit screen)
    const modelId = action.payload?.formdata?.find(
      (f) => f.key === "model_id",
    )?.value;

    if (modelId) {
      yield put(auditorActions.fetchModelDetails({ id: modelId }));
    }
  }, options);
}

function* fetchModelDetails({ payload }) {
  const { id } = payload;
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get(`/LM/model_details?id=${id}`);
    if (res?.success) {
      const model = res?.data || null; // model object is inside `data`
      // UI needs icon/controls and they are outside `data`
      const normalized = model
        ? {
            ...model,
            controls: res?.controls ?? model.controls,
            icon: res?.icone ?? model.icon, // note: backend key is `icone`
          }
        : null;

      if (normalized) {
        yield put(auditorActions.setModelDetails(normalized));
      } else {
        yield put(auditorActions.setModelDetailsError("Model not found"));
      }
    } else {
      yield put(
        auditorActions.setModelDetailsError(res?.error || "Model not found"),
      );
    }
  }, "Failed to fetch Model Details");
}

function* deactivateModel({ payload }) {
  const { id, password } = payload;

  yield requestHandler(
    function* () {
      const encodedPassword = encodeURIComponent(password.trim());

      const res = yield NMAxiosInst.patch(
        `/LM/${id}/deactivate?current_password=${encodedPassword}`,
      );

      if (!res?.success) {
        throw new Error(res?.error || "Failed to deactivate model");
      }

      yield setAlertAndLoading(null, {
        message: res.message || "Blueprint deactivated successfully.",
      });

      yield put(auditorActions.fetchModelList());
      yield put(auditorActions.deactivateModelSuccess());
    },
    {
      errorMessage: "Failed to deactivate model",
      action: { payload },
    },
  );
}

function* reactivateModel({ payload }) {
  const { id, password } = payload;

  yield requestHandler(
    function* () {
      const encodedPassword = encodeURIComponent(password.trim());

      const res = yield NMAxiosInst.patch(
        `/LM/${id}/activate?current_password=${encodedPassword}`,
      );

      if (!res?.success) {
        throw new Error(res?.error || "Failed to reactivate model");
      }

      yield setAlertAndLoading(null, {
        message: res.message || "Blueprint reactivated successfully.",
      });

      yield put(auditorActions.fetchModelList());
      yield put(auditorActions.fetchModelDetails({ id }));
      yield put(auditorActions.reactivateModelSuccess());
    },
    {
      errorMessage: "Failed to reactivate model",
      action: { payload },
    },
  );
}

function* allocateModelToOrgs(action) {
  const options = {
    errorMessage: "Failed to allocate blueprint",
    action: action,
  };

  yield requestHandler(function* () {
    const { payload } = action;
    yield NMAxiosInst.post("/LM/mark_as_subscribed", payload);

    yield setAlertAndLoading(null, {
      message: "Audit Blueprint Allocated Successfully",
    });

    // refresh list + details
    yield put(auditorActions.fetchModelList());
    const id = payload?.model_id;
    if (id) yield put(auditorActions.fetchModelDetails({ id }));

    // yield put(auditorActions.allocateModelToOrgsSuccess());
  }, options);
}

function* createAuditReportBlueprint(action) {
  const options = {
    errorMessage: "Failed to create report blueprint",
    action: action,
  };
  yield requestHandler(function* () {
    const { payload } = action;
    yield NMAxiosInst.post("/LM/create_audit_report_blueprint", payload);

    yield setAlertAndLoading(null, {
      message: "Blueprint created Successfully",
    });
  }, options);
}

function* updateAuditReportBlueprint(action) {
  const options = {
    errorMessage: "Failed to update report blueprint",
    action: action,
  };
  yield requestHandler(function* () {
    const { payload } = action;
    yield NMAxiosInst.put("/LM/update_audit_report_blueprint", payload);
    yield setAlertAndLoading(null, {
      message: "Blueprint updated Successfully",
    });
  }, options);
}

function* fetchAuditReportBlueprint(action) {
  const options = {
    errorMessage: "Failed to update report blueprint",
    action: action,
  };
  yield requestHandler(function* () {
    const { blueprintId } = action.payload;
    const bp = yield NMAxiosInst.get(
      "/LM/audit_report_blueprint?guid=" + blueprintId,
    );
    yield put(auditorActions.setCurrentAuditReport(bp.data));
    return bp ? bp.data : null;
  }, options);
}

function* fetchAuditReportBlueprints(action) {
  const options = {
    errorMessage: "Failed to update report blueprint",
    action: action,
  };
  yield requestHandler(function* () {
    const res = yield NMAxiosInst.get("/LM/audit_report_blueprints");
    let blueprints = res?.data || [];
    blueprints = blueprints.filter((bp) => bp.status !== "deleted");
    const statusOrder = { active: 0, draft: 1, archive: 2 };
    blueprints.sort((a, b) => {
      const statusDiff =
        (statusOrder[a.status] ?? 0) - (statusOrder[b.status] ?? 0);
      return statusDiff;
    });
    yield put(auditorActions.setAuditReportsBlueprints(blueprints));
  }, options);
}

SagaRegistry.register(function* auditorSaga() {
  yield takeEvery("auditor/fetchModelList", fetchModelList);
  yield takeEvery("auditor/fetchReportFormats", fetchReportFormats);
  yield takeEvery("auditor/fetchModelDetails", fetchModelDetails);
  yield takeEvery("auditor/createModel", createModel);
  yield takeEvery("auditor/updateModel", updateModel);
  yield takeEvery("auditor/deactivateModel", deactivateModel);
  yield takeEvery("auditor/reactivateModel", reactivateModel);
  yield takeEvery("auditor/allocateModelToOrgs", allocateModelToOrgs);
  yield takeEvery(
    "auditor/fetchAuditReportBlueprints",
    fetchAuditReportBlueprints,
  );
  yield takeEvery(
    "auditor/fetchAuditReportBlueprint",
    fetchAuditReportBlueprint,
  );
  yield takeEvery(
    "auditor/createAuditReportBlueprint",
    createAuditReportBlueprint,
  );
  yield takeEvery(
    "auditor/updateAuditReportBlueprint",
    updateAuditReportBlueprint,
  );
});
